import { Location } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { concat, concatMap, forkJoin, map, Observable, of } from 'rxjs';
import { TeamCodexServiceService } from './services/team-codex-service.service';
import { DataManipulationService } from 'src/app/services/data-manipulation.service';

interface CodexReport {
  uuid: string,
  name: string,
  index: number,
  team: any,
  score: {
    value: number,
    trend: any,
    color: string,
    diff: {
      value: number,
      trend: any
    }
  },
  progress: any,
  highestTopic: any,
  lowestTopic: any,
  details: any,
  questions: any[]
}

@Component({
  selector: 'app-leadership-codex-team-dashboard',
  templateUrl: './leadership-codex-team-dashboard.component.html',
  styleUrls: ['./leadership-codex-team-dashboard.component.scss']
})
export class LeadershipCodexTeamDashboardComponent implements OnInit {

  codexView: string = 'quarter';
  scoreColor: string = '#A0ADCE';
  sortingOrder: string = '';
  indicatorSortingOrder: string = '';
  selectedCodexTeamName: string = '';

  indicatorInterval: any[] = [];
  teamCodexReports: any[] = [];
  codexQuestions: any[] = [];
  codexesByCategory: any[] = [];

  teamUuid: any;
  activeQuarterParam: any;
  currentReport: any;
  previousReport: any;
  activeReport: any;

  showCategoryStatements: boolean = false;

  fade: Record<string, string> = {
    green: '#DBF1EA',
    yellow: '#F3EEC7',
    orange: '#FFE7C8',
    red: '#FEE5E5',
    grey: '#E3E8F6'
  }

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private location: Location,
    private codexService: TeamCodexServiceService,
    private data: DataManipulationService)
  { }

  ngOnInit(): void {
    this.data.showLoading();
    this.teamUuid = this.route.snapshot.paramMap.get('teamUuid');
    this.selectedCodexTeamName = this.data.userRole.teams.find((team: any) => team.team.uuid === this.teamUuid)?.team.name;
    this.activeQuarterParam = history.state.activeQuarter;
    this.fetchData();
  }

  fetchData() {
    this.getIndicatorInterval().pipe(
      concatMap(() => this.getTeamLeadershipCodex()),
      concatMap((reports: any[]) => {
        this.teamCodexReports = reports;
        this.activeReport = this.teamCodexReports.find((report: any) => report.uuid === this.activeQuarterParam) || this.teamCodexReports[0];
        const firstUuid = this.activeReport?.uuid;

        return this.getCodexQuestions(firstUuid, this.sortingOrder).pipe(
          map(codexQuestions => ({
            reports,
            codexQuestions
          }))
        )

      })
    ).subscribe((res: any) => {
      this.codexQuestions = res.codexQuestions;
      this.applyReport(this.activeReport.index);
    });
  }

  getIndicatorInterval(): Observable<any[]> {
    return this.codexService.getIndicatorInterval().pipe(
      map((res: any) => {
        this.indicatorInterval = [...res]
        return this.indicatorInterval;
      })
    );
  }

  getTeamLeadershipCodex(): Observable<any> {
    return this.codexService.getTeamLeadershipCodexReports(this.teamUuid).pipe(
      map((res: any) => res._embedded['team-codex-reports'] ?? []),
      map((reports : any) => {
        return reports.map((report: any, index: number) => {
          const {score, details, ...rest} = report;
          const previousReport = reports[index + 1];
          const previousDetailsMap = new Map(
            (previousReport?.details ?? []).map((detail: any) => [
              detail.category.uuid,
              detail
            ])
          );
          return {
            ...rest,
            index: index,
            score: {
              value: score,
              color: this.getTeamLeadershipCodexIncidatorColor(score),
              //@ts-ignore
              hexColor: this.data.colors[`${this.getTeamLeadershipCodexIncidatorColor(score)}`]
            },
            progress: {
              value: report.progress,
              trend: (report.progress !== 0) ? report.progress > 0 ? 'up' : 'down' : null
            },
            details: details?.map((indicator: any) => {
              const previousIndicator: any = previousDetailsMap.get(indicator.category.uuid);
              return {
                ...indicator,
                fadeColor: this.fade[`${this.getTeamLeadershipCodexIncidatorColor(indicator.score)}`],
                color: this.getTeamLeadershipCodexIncidatorColor(indicator.score),
                trend: previousIndicator ?
                (indicator.score > previousIndicator.score) ? 'up' :
                (indicator.score < previousIndicator.score) ? 'down': null : null
              }
            }) ?? []
          }
        })
      })
    )
  }

  getCodexQuestions(reportUuid: string, order: string) {
    return this.codexService
    .getTeamLeadershipCodexQuestions(this.teamUuid, reportUuid, order)
    .pipe(
      map((res: any) => res._embedded['team-codex-questions'] ?? []),
      map((questions: any[]) => {
        return questions.map((question: any) => ({
          ...question,
          fadeColor: this.fade[`${this.getTeamLeadershipCodexIncidatorColor(question.score)}`],
          //@ts-ignore
          gradientColor: this.data.colors[`${this.getTeamLeadershipCodexIncidatorColor(question.score)}Gradient`],
          //@ts-ignore
          color: this.data.colors[`${this.getTeamLeadershipCodexIncidatorColor(question.score)}`],
          details: question.details?.map((detail: any) => {
            const highestPercentage = Math.max(...question.details.map((d: any) => d.percentage));
            return {
              ...detail,
              isHighest: detail.percentage === highestPercentage,
              color: this.fade[`${this.getTeamLeadershipCodexIncidatorColor(detail.percentage)}`]
            }
          })
        }))
      })
    );
  }

  setCurrentReportData(currentReport: any, previousReport: any): CodexReport {
    const {highest, lowest} = this.sortTopics(currentReport.details);
    const {highest: prevHighest, lowest: prevLowest} = previousReport ? this.sortTopics(previousReport.details): {highest: null, lowest: null};
    return {
      uuid: currentReport.uuid,
      name: currentReport.quarter,
      index: currentReport.index,
      team: currentReport.team,
      highestTopic: {
        ...highest,
        comparison: this.compareTopics(highest, prevHighest),
        //@ts-ignore
        gradientColor: this.data.colors[`${highest.color}Gradient`]
      },
      lowestTopic: {
        ...lowest,
        comparison: this.compareTopics(lowest, prevLowest),
        //@ts-ignore
        gradientColor: this.data.colors[`${lowest.color}Gradient`]
      },
      score: {
        value: currentReport.score.value,
        trend: previousReport ? this.getTrendBasedOnScores(currentReport.score.value, previousReport.score.value) : null,
        //@ts-ignore
        color: this.data.colors[currentReport.score.color],
        diff: {
          value: previousReport ? Math.round(currentReport.score.value - previousReport.score.value) : 0,
          trend: previousReport ? ((currentReport.score.value - previousReport.score.value) > 0 ? 'up' : 'down') : null
        }
      },
      progress: currentReport.progress,
      details: currentReport.details.map((indicator: any) => ({
        ...indicator,
        //@ts-ignore
        gradientColor: this.data.colors[`${indicator.color}Gradient`]
      })),
      questions: this.codexQuestions
    }
  }

  questionCategoryChanged(changes: any) {
    this.indicatorSortingOrder = changes.order ? changes.order : '';
    this.getTeamLeadershipCodexQuestionsByCategoryUuid(changes.categoryUuid, this.indicatorSortingOrder)
  }

  getTeamLeadershipCodexQuestionsByCategoryUuid(categoryUuid: string, order: string) {
    this.codexService.getTeamLeadershipCodexQuestionsByCategoryUuid(this.teamUuid, categoryUuid, order).pipe(
      map((result: any) => result._embedded['team-question-leadership-codexes-by-category'] ?? []),
      map((codexesByCategory: any[]) => {
        return codexesByCategory.map((codex: any) => ({
          ...codex,
          fadeColor: this.fade[`${this.getTeamLeadershipCodexIncidatorColor(codex.score)}`],
          //@ts-ignore
          gradientColor: this.data.colors[`${this.getTeamLeadershipCodexIncidatorColor(codex.score)}Gradient`],
          //@ts-ignore
          color: this.data.colors[`${this.getTeamLeadershipCodexIncidatorColor(codex.score)}`],
          details: codex.details?.map((detail: any) => {
            const highestPercentage = Math.max(...codex.details.map((d: any) => d.percentage));
            return {
              ...detail,
              isHighest: detail.percentage === highestPercentage,
              color: this.fade[`${this.getTeamLeadershipCodexIncidatorColor(detail.percentage)}`]
            }
          })
        }))
      })
    ).subscribe((res: any) => {
      this.codexesByCategory = res;
      this.showCategoryStatements = true;
    })
  }

  sortTopics(topics: any) {
    if (!topics?.length) return { highest: null, lowest: null };

    return topics.reduce((acc: any, topic: any) => {
      if (topic.score > acc.highest.score) acc.highest = topic;
      if (topic.score < acc.lowest.score) acc.lowest = topic;
      return acc;
    }, {
      highest: topics[0],
      lowest: topics[0]
    });
  }

  compareTopics(current: any, previous: any) {
    if (!current || !previous) {
      return {
        value: current?.score ?? null,
        diff: 0,
        trend: null
      };
    }

    const diff = current.score - previous.score;

    return {
      value: current.score,
      diff: Math.round(diff),
      trend: diff > 0 ? 'up' : diff < 0 ? 'down' : null
    };
  }

  sortQuarterQuestions(order: string | null) {
    this.sortingOrder = order ? order : '';
    this.getCodexQuestions(this.currentReport.uuid, this.sortingOrder).subscribe((res: any) => {
      this.currentReport.questions = res;
    })
  }

  getIndicatorsTrendBasedOnScore() {

  }

  getTrendBasedOnScores(currentScore: number, previousScore: number): string {
    return currentScore > previousScore ? 'up' : 'down';
  }

  private processReports(reports: any[], index: number) {
    const current = reports[index] ? this.setCurrentReportData(reports[index], reports[index + 1]) : null;
    const previous = reports[index + 1] ?? null;

    return { current, previous };
  }

  applyReport(index: number) {
    const { current, previous } = this.processReports(this.teamCodexReports, index);

    this.currentReport = current;
    this.previousReport = previous;
    this.data.hideLoading();
  }

  onReportChange(index: number) {
    this.applyReport(index);
    const reportUuid = this.currentReport.uuid;
    this.getCodexQuestions(reportUuid, this.sortingOrder).subscribe((questions: any[]) => {
      this.currentReport.questions = questions;
    })
  }

  getTeamLeadershipCodexIncidatorColor(score: number): string | null {
    if (score === 0) return 'grey';
    const interval = this.indicatorInterval.find(interval => interval.min <= score && score <= interval.max);
    return interval.indicatorColor.color ? interval.indicatorColor.color : null;
  }

  setCodexView(view: any) {
    this.codexView = view;
    if (view === 'indicator') this.getTeamLeadershipCodexQuestionsByCategoryUuid(this.currentReport.details[0].category.uuid, this.indicatorSortingOrder);
  }

  goBackToTeam() {
    this.router.navigate(['team', this.teamUuid]);
  }

  goBack() {
    this.location.back();
  }

}
